import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, Lock } from 'lucide-react';

interface HotspotAdminDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const HotspotAdminDialog = ({ open, onOpenChange, onSuccess }: HotspotAdminDialogProps) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!code.trim()) {
      toast.error('Please enter the admin code');
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('verify-hotspot-admin', {
        body: { code: code.trim() },
      });

      if (error) throw error;

      if (data.success) {
        sessionStorage.setItem('hotspot_admin_token', data.sessionToken);
        toast.success('Admin access granted');
        setCode('');
        onOpenChange(false);
        onSuccess();
      } else {
        toast.error(data.error || 'Invalid code');
      }
    } catch (error) {
      console.error('Error verifying admin code:', error);
      toast.error('Failed to verify code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Hotspot Editor Access
          </DialogTitle>
          <DialogDescription>
            Enter the admin code to edit hero section hotspots.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="admin-code">Admin Code</Label>
            <Input
              id="admin-code"
              type="password"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter admin code"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Unlock Editor
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
